import MapsUgcIcon from "@mui/icons-material/MapsUgc";
import { Box, CircularProgress, Popover } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import Flex from "@/components/Flex";
import OutlineCard from "@/components/OutlineCard";
import SearchInput from "@/components/SearchInput";
import { SegmentTabs } from "@/components/SegmentTabs";
import Text from "@/components/Text";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import { useDebounce } from "@/hooks/use-debounce";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useConversationCreate } from "@/queries/communication/chat/convo/use-conversation-create";
import { useSearchPatientList } from "@/queries/list/useSearchPatientList";
import { useProviderListV2 } from "@/queries/use-provider-list-v2";
import {
  DGT_Pagination,
  getXtenantId,
  showToastOnApiCall,
} from "@/utils/utils";
import {
  ACTIVE_PROVIDER_FILTER,
  CHAT_TABS,
  ConvoTab,
  RawConvo,
  mapToNameEmail,
} from "./chat-constant";

interface NewChatPopOverProps {
  convoOnlyWithProvider?: boolean;
}

export const NewChatPopOver = ({
  convoOnlyWithProvider = false,
}: NewChatPopOverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeTab, setActiveTab] = useState<ConvoTab>(
    !convoOnlyWithProvider ? "provider" : "patient"
  );
  const open = Boolean(anchorEl);
  const [searchKey, setSearchKey, instantSearchKey] = useDebounce<string>("");
  const [pagination, setPagination] = useState<DGT_Pagination>({
    page: 0,
    pageSize: 5,
  });
  const { mutateAsync: createConversation } = useConversationCreate();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const onlyProviderConvo = activeTab === "provider" || convoOnlyWithProvider;

  const handleCreateConversation = async (rawConvo: any) => {
    const payload = {
      participant2: rawConvo.uuid,
      participant2Provider: onlyProviderConvo,
    };

    showToastOnApiCall({
      apiCall: createConversation(payload),
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["conversation-list"],
        });
      },
    });
  };

  const { data: providersList, isFetching: isFetchingProviders } =
    useProviderListV2({
      pagination,
      searchString: searchKey,
      xTENANTID: getXtenantId(),
      enabled: open && onlyProviderConvo,
      ...ACTIVE_PROVIDER_FILTER,
    });

  const { data: patientList, isFetching: isFetchingPatients } =
    useSearchPatientList({
      pagination,
      searchString: searchKey,
      xTENANTID: getXtenantId(),
      disabled: onlyProviderConvo,
    });

  const activeData = onlyProviderConvo ? providersList : patientList;
  const { items, onScroll } = useInfiniteScroll<any>({
    pagination,
    setPagination,
    content: activeData?.data?.content,
    totalElements: activeData?.data?.page?.totalElements,
    isFetching: onlyProviderConvo ? isFetchingProviders : isFetchingPatients,
    dedupeKey: "uuid",
  });

  const rawConversations = useMemo(
    () => items?.map(mapToNameEmail) as RawConvo[],
    [items]
  );

  const resetPaging = () => setPagination((p) => ({ ...p, page: 0 }));

  return (
    <Flex flexDirection={"column"} gap={1}>
      <StyledButtonV2
        label={"New Chat"}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setAnchorEl(e.currentTarget as HTMLElement);
        }}
        sIcon={<MapsUgcIcon fontSize="medium" />}
      />

      <Popover
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Flex flexDirection={"column"} gap={1} p={1}>
          <SearchInput
            searchKey={instantSearchKey}
            onSearch={(v) => {
              setSearchKey(v);
              resetPaging();
            }}
            placeholder={`Search ${onlyProviderConvo ? "Provider" : "Patient"}`}
          />
          {!convoOnlyWithProvider && (
            <SegmentTabs
              tabsData={CHAT_TABS}
              value={activeTab}
              onChange={(tab) => {
                setActiveTab(tab as ConvoTab);
                setSearchKey("");
                resetPaging();
              }}
            />
          )}

          {/* // render  all RAW convo to create new SOLID convo */}
          <Box
            onScroll={(e) => onScroll(e.currentTarget)}
            sx={{ maxHeight: "18rem", overflowY: "auto" }}
          >
            <Flex flexDirection={"column"} gap={1}>
              {rawConversations?.map((rawConvo) => (
                <OutlineCard key={rawConvo.uuid}>
                  <Flex
                    flexDirection={"column"}
                    gap={0}
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleCreateConversation(rawConvo)}
                  >
                    <Text>{rawConvo?.name}</Text>
                    <Text>{rawConvo?.email}</Text>
                  </Flex>
                </OutlineCard>
              ))}
            </Flex>
            {(onlyProviderConvo ? isFetchingProviders : isFetchingPatients) && (
              <Flex justifyContent="center" sx={{ py: 1 }}>
                <CircularProgress size={18} />
              </Flex>
            )}
          </Box>
        </Flex>
      </Popover>
    </Flex>
  );
};
